import React, { useState, useEffect } from 'react';
import { Select, Spin } from 'antd';
import { dealerService } from '../services/dealerService';
import { Dealer } from '../types';
import { useAuth } from '../contexts/AuthContext';

const { Option } = Select;

interface DealerSelectorProps {
  value?: number;
  onChange?: (value: number) => void;
  placeholder?: string;
  allowClear?: boolean;
  style?: React.CSSProperties;
  disabled?: boolean;
  status?: number; // 筛选状态：1-启用，0-禁用
  level?: number; // 筛选等级
}

/**
 * 经销商选择器组件
 * 只有超级经销商（dealerType=1）才能看到此组件
 * 普通经销商则不渲染此组件
 */
const DealerSelector: React.FC<DealerSelectorProps> = ({
  value,
  onChange,
  placeholder = '请选择经销商',
  allowClear = true,
  style,
  disabled = false,
  status,
  level,
}) => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [dealers, setDealers] = useState<Dealer[]>([]);

  // 判断当前用户是否为超级经销商
  const isSuperDealer = user?.dealerType === 1;

  // 加载经销商列表
  useEffect(() => {
    // 只有超级经销商才加载数据
    if (isSuperDealer) {
      loadDealers();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, level, isSuperDealer]);

  const loadDealers = async () => {
    try {
      setLoading(true);

      // 如果有指定状态或等级，使用条件查询
      if (status !== undefined || level !== undefined) {
        const response = await dealerService.queryDealers({
          status,
          dealerLevel: level,
          pageNo: 0,
          pageSize: 1000, // 获取所有数据
        });
        if (response.data) {
          setDealers(response.data.content || []);
        }
      } else {
        // 否则获取所有启用的经销商
        const response = await dealerService.getDealersByStatus(1);
        if (response.data) {
          setDealers(response.data);
        }
      }
    } catch (error) {
      console.error('加载经销商列表失败:', error);
      setDealers([]);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (selectedValue: number) => {
    if (onChange) {
      onChange(selectedValue);
    }
  };

  // 如果不是超级经销商，不渲染组件
  if (!isSuperDealer) {
    return null;
  }

  return (
    <Select
      value={value}
      onChange={handleChange}
      placeholder={placeholder}
      allowClear={allowClear}
      style={{ width: 250, ...style }}
      disabled={disabled}
      loading={loading}
      showSearch
      optionFilterProp="children"
      filterOption={(input, option) =>
        (option?.children as unknown as string)
          ?.toLowerCase()
          ?.includes(input.toLowerCase())
      }
      notFoundContent={loading ? <Spin size="small" /> : '暂无数据'}
    >
      {dealers.map(dealer => (
        <Option key={dealer.id} value={dealer.id}>
          {dealer.dealerName} ({dealer.dealerCode})
        </Option>
      ))}
    </Select>
  );
};

export default DealerSelector;

